// src/services/api.js

import { auth } from "../firebaseConfig";
import { sendPasswordResetEmail } from "firebase/auth";

/**
 * MakeMyVity Parent Portal — API Service Layer
 *
 * Typed fetch wrappers for all backend endpoints.
 * Each function maps to a specific hook mutation in useYearbookState.
 *
 * Auth rule:
 * - Do NOT store Firebase ID token in localStorage.
 * - For normal API calls, read a fresh token from Firebase Auth.
 * - For /auth/login, use the explicit token passed from the store right after Firebase sign-in.
 */

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://db-pg.makemyvity.com/api/v1";

const CARTOON_API_BASE_URL = "https://extensions.makemyvity.com/cartoon/api";
// const CARTOON_API_BASE_URL = "http://localhost:8000/api";


const APP_ID = "makemyvity";

// =============================================================================
// Auth headers
// =============================================================================

export async function getAuthHeaders() {
  const firebaseUser = auth.currentUser;

  if (!firebaseUser) {
    return {};
  }

  const token = await firebaseUser.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
  };
}

// =============================================================================
// Fetch helper
// =============================================================================

async function request(path, options = {}) {
  const baseUrl = options.baseUrl || BASE_URL;

  const authHeaders = await getAuthHeaders();

  const isFormData =
    typeof FormData !== "undefined" && options.body instanceof FormData;

  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...authHeaders,
    "X-App-Id": APP_ID,
    ...(options.headers || {}),
  };

  const res = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ detail: res.statusText }));
    const detail = error?.detail;

    const message =
      typeof detail === "object" && detail !== null
        ? detail.message
        : detail || error.message || "Request failed";

    const err = new Error(message);
    err.status = res.status;
    err.code =
      (typeof detail === "object" && detail !== null
        ? detail.code
        : undefined) || error.code;
    err.limitKey =
      typeof detail === "object" && detail !== null
        ? detail.limit_key
        : undefined;
    err.limit =
      typeof detail === "object" && detail !== null
        ? detail.limit
        : undefined;
    err.data = error;
    err.detail = detail;

    throw err;
  }

  if (res.status === 204) return null;

  const text = await res.text();
  return text ? JSON.parse(text) : null;
}


 async function _get(path) {
    return request(path);
  }

  async function _patch(path, body) {
    return request(path, {
      method: "PATCH",
      body: JSON.stringify(body),
    });
  }

  async function _post(path, body) {
    const isFormData = body instanceof FormData;

    return request(path, {
      method: "POST",
      body: isFormData ? body : JSON.stringify(body),
    });
  }

  async function _delete(path, body = null) {
    const options = {
      method: "DELETE",
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return request(path, options);
  }


// =============================================================================
// 1. BOOTSTRAP / AUTH
// =============================================================================

/**
 * Backend login bridge.
 *
 * Store should call this after Firebase sign-in/signup/provider login:
 *   const firebaseToken = await firebaseUser.getIdToken(true);
 *   const backendData = await apiBackendLogin(firebaseToken);
 */
export async function apiBackendLogin(firebaseToken) {
  if (!firebaseToken) {
    throw new Error("firebaseToken is required for backend login");
  }

  const res = await fetch(`${BASE_URL}/auth/login`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${firebaseToken}`,
      "Content-Type": "application/json",
      "X-App-Id": APP_ID,
    },
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    const detail = data?.detail;
    const message =
      typeof detail === "object" && detail !== null
        ? detail.message
        : detail || "Backend login failed.";

    const err = new Error(
      typeof message === "string" ? message : JSON.stringify(message)
    );
    err.status = res.status;
    err.code =
      typeof detail === "object" && detail !== null
        ? detail.code
        : data?.code;
    err.data = data;
    err.detail = detail;

    throw err;
  }

  return data;
}

// Deprecated. Firebase login/signup now belongs in the Zustand auth store.
export async function apiLogin() {
  throw new Error(
    "apiLogin is deprecated. Use Firebase auth in the store, then apiBackendLogin()."
  );
}

export async function apiSignup() {
  throw new Error(
    "apiSignup is deprecated. Use Firebase auth in the store, then apiBackendLogin()."
  );
}

export async function apiForgotPassword(email) {
  const normalizedEmail = String(email || "").trim().toLowerCase();

  if (!normalizedEmail) {
    throw new Error("Email is required");
  }

  const resetRedirectUrl =
    process.env.REACT_APP_PASSWORD_RESET_REDIRECT_URL ||
    "https://makemyvity.com/login";

  await sendPasswordResetEmail(auth, normalizedEmail, {
    url: resetRedirectUrl,
    handleCodeInApp: false,
  });

  return {
    success: true,
    message: "Reset link sent",
  };
}

export async function getMe() {
  return request("/parent/me");
}

export async function apiGetParentMe() {
  return getMe();
}

export async function getProjectDashboard(projectId) {
  return request(`/parent/projects/${projectId}/dashboard`);
}

// =============================================================================
// 2. CHILD DETAILS
// =============================================================================

export async function updateChild(personId, updates) {
  return request(`/persons/${personId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
}

// =============================================================================
// 3. PERSONAL PHOTOS & CAPTIONS
// =============================================================================

export async function presignParentPhotos(projectId, files) {
  if (!projectId) {
    throw new Error("projectId is required for parent photo presign");
  }

  return request(`/parent/projects/${projectId}/photos/presign`, {
    method: "POST",
    body: JSON.stringify({
      purpose: "parent_photo",
      project_id: projectId,
      files,
    }),
  });
}

export async function uploadToPresignedUrl(url, file, contentType) {
  const res = await fetch(url, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });

  if (!res.ok) throw new Error("Upload failed");
}

export async function confirmUpload(ingestionJobId, filename) {
  return request("/upload/confirm", {
    method: "POST",
    body: JSON.stringify({
      ingestion_job_id: ingestionJobId,
      filename,
    }),
  });
}

export async function getParentPhotos(projectId) {
  return request(`/parent/projects/${projectId}/photos`);
}

export async function deleteParentPhoto(projectId, mediaItemId) {
  return request(`/parent/projects/${projectId}/photos/${mediaItemId}`, {
    method: "DELETE",
  });
}

// =============================================================================
// 4. PERSONAL PHOTO CAPTIONS
// =============================================================================

export async function getCaption(projectId, projectMediaId) {
  return request(
    `/parent/projects/${projectId}/photos/${projectMediaId}/caption`
  );
}

export async function createCaption(projectId, projectMediaId, caption) {
  return request(
    `/parent/projects/${projectId}/photos/${projectMediaId}/caption`,
    {
      method: "POST",
      body: JSON.stringify({ caption }),
    }
  );
}

export async function updateCaption(projectId, projectMediaId, caption) {
  return request(
    `/parent/projects/${projectId}/photos/${projectMediaId}/caption`,
    {
      method: "PATCH",
      body: JSON.stringify({ caption }),
    }
  );
}

export async function deleteCaption(projectId, projectMediaId) {
  return request(
    `/parent/projects/${projectId}/photos/${projectMediaId}/caption`,
    {
      method: "DELETE",
    }
  );
}

// =============================================================================
// 5. PERSONAL LETTER
// =============================================================================

export async function getLetters(projectId) {
  const res = await request(`/parent/projects/${projectId}/content?type=personal_note`);
  return res.items || [];
}

// Backward-compatible helper for callers that still expect the first letter.
export async function getLetter(projectId) {
  const letters = await getLetters(projectId);
  return letters?.[0] || null;
}

export async function createLetter(projectId, text) {
  return request(`/parent/projects/${projectId}/content`, {
    method: "POST",
    body: JSON.stringify({
      type: "personal_note",
      data: { text },
      order: 0,
    }),
  });
}

export async function updateLetter(projectId, blockId, text) {
  return request(`/parent/projects/${projectId}/content/${blockId}`, {
    method: "PUT",
    body: JSON.stringify({
      data: { text },
      order: 0,
    }),
  });
}

export async function deleteLetter(projectId, blockId) {
  return request(`/parent/projects/${projectId}/content/${blockId}`, {
    method: "DELETE",
  });
}

// Legacy singleton save. Kept so older screens do not break while the letter UI
// moves to multi-letter content blocks.
export async function saveLetter(projectId, text) {
  return request(`/projects/${projectId}/content/singleton/letter`, {
    method: "PUT",
    body: JSON.stringify({ data: { text } }),
  });
}

// =============================================================================
// 6. GROWING UP MEMORIES
// =============================================================================

export async function getMemories(projectId) {
  const res = await request(`/projects/${projectId}/content?type=memory`);
  return res.items || [];
}

export async function createMemory(projectId, { title, value, promptId }) {
  return request(`/projects/${projectId}/content`, {
    method: "POST",
    body: JSON.stringify({
      content_type: "memory",
      data: { title, text: value, prompt_id: promptId || null },
    }),
  });
}

export async function updateMemory(projectId, blockId, { value }) {
  return request(`/projects/${projectId}/content/${blockId}`, {
    method: "PUT",
    body: JSON.stringify({ data: { text: value } }),
  });
}

export async function deleteMemory(projectId, blockId) {
  return request(`/projects/${projectId}/content/${blockId}`, {
    method: "DELETE",
  });
}

export async function reorderMemories(projectId, orderedIds) {
  return request(`/projects/${projectId}/content/reorder?type=memory`, {
    method: "PATCH",
    body: JSON.stringify({ ids: orderedIds }),
  });
}

// =============================================================================
// 7. SCHOOL PHOTOS
// =============================================================================

export async function getSchoolPhotos(projectId) {
  return request(`/parent/projects/${projectId}/school-photos`);
}

export async function updateSchoolPhotoSelections(
  projectId,
  { select = [], deselect = [] } = {}
) {
  if (!projectId) {
    throw new Error("projectId is required to update school photo selections");
  }

  return request(`/parent/projects/${projectId}/school-photos/selections`, {
    method: "POST",
    body: JSON.stringify({ select, deselect }),
  });
}

// =============================================================================
// 8. BOOK COVER
// =============================================================================

export async function updateCover(projectId, coverOptionId) {
  return request(`/parent/projects/${projectId}/cover`, {
    method: "PATCH",
    body: JSON.stringify({ cover_option_id: coverOptionId }),
  });
}

// =============================================================================
// 9. SHIPPING
// =============================================================================

export async function getAddresses() {
  return request("/addresses");
}

export async function getDefaultShippingAddress() {
  return request("/addresses/default?address_type=SHIPPING");
}

export async function createAddress(address) {
  return request("/addresses", {
    method: "POST",
    body: JSON.stringify({ ...address, address_type: "shipping" }),
  });
}

export async function updateAddress(addressId, address) {
  return request(`/addresses/${addressId}`, {
    method: "PATCH",
    body: JSON.stringify(address),
  });
}

// =============================================================================
// 10. ORDER
// =============================================================================

export async function updateOrderQuantity(projectId, quantity) {
  return request(`/parent/projects/${projectId}/order`, {
    method: "PATCH",
    body: JSON.stringify({ quantity }),
  });
}

export async function createCheckout(projectId, quantity) {
  return request(`/parent/projects/${projectId}/order/checkout`, {
    method: "POST",
    body: JSON.stringify({ quantity }),
  });
}

/**
 * Amend a previously paid physical yearbook order.
 * The backend calculates the delta and creates a Stripe PaymentIntent only
 * when the change increases the paid amount. Replaying the exact request with
 * the same idempotencyKey is also how the client polls for webhook application.
 */
export async function createOrderAmendment(orderId, payload) {
  if (!orderId) {
    throw new Error("orderId is required for an order amendment");
  }

  return request(`/parent/orders/${encodeURIComponent(orderId)}/amendment`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// =============================================================================
// 11. STRIPE CHECKOUT / YEARBOOK PLUS
// =============================================================================

/**
 * Get checkout quote totals before processing payment.
 * Payload requires: project_id, quantity, ship_to, selected_upgrade_keys
 */
export async function getCheckoutQuote(payload) {
  return request("/checkout/quote", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Create a Stripe PaymentIntent for the client to confirm.
 * Payload requires: project_id, quantity, ship_to, selected_upgrade_keys,
 * idempotency_key
 */
export async function createStripePaymentIntent(payload) {
  return request("/payments/stripe/payment-intent", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/**
 * Create a PaymentIntent for upgrading a project.
 * Returns Stripe client secret for use with Elements.
 *
 * @param {string} projectId
 * @param {object} selectedOffer - backend-provided upgrade offer object
 * @returns {{ client_secret: string, amount_cents: number, currency?: string, offer_id?: string }}
 */
export async function createUpgradePaymentIntent(
  projectId,
  selectedOffer,
  options = {}
) {
  const offerId = selectedOffer?.offer_id;

  if (!projectId) {
    throw new Error("Missing projectId");
  }

  if (!offerId) {
    throw new Error("Missing upgrade offer_id");
  }

  const checkoutMode = options.checkoutMode || "upgrade";

  return request("/payments/stripe/payment-intent", {
    method: "POST",
    body: JSON.stringify({
      checkout_mode: checkoutMode,

      project_id: projectId,

      // Backend should validate this against project.catalog_offer_id.
      // Null is fine if backend resolves it from project.
      catalog_offer_id: options.catalogOfferId || null,

      // Upgrade checkout itself represents one purchase attempt.
      // Backend determines actual physical-book count for per-copy pricing.
      quantity: 1,

      // Upgrade is not a new shipment.
      ship_to: null,
      shipping_address_id: null,

      billing_address_id: options.billingAddressId || null,

      line_items: [
        {
          offer_id: offerId,
          quantity: options.upgradeQuantity || 1,
        },
      ],

      promotion_code: options.promotionCode || null,

      idempotency_key:
        options.idempotencyKey ||
        `upgrade_${projectId}_${offerId}_${crypto.randomUUID()}`,
    }),
  });
}

export async function finalizeStripePayment(paymentIntentId) {
  return request("/payments/stripe/finalize", {
    method: "POST",
    body: JSON.stringify({
      payment_intent_id: paymentIntentId,
    }),
  });
}


export async function getCartoons({ gender, race, eyeColor, hairColor, hairStyle, ageGroup } = {}) {
    const params = {
        gender,
        race,
        eyeColor,
        hairColor,
        hairStyle,
        ageGroup
    };
    const query = new URLSearchParams(params);

    const customBaseUrl = "https://extensions.makemyvity.com/cartoon/api";

  return request("/cartoons?", { baseUrl: customBaseUrl });
}

export async function getOrderItems(orderItemId) {
  return request(`/admin/preschool/order-items/${orderItemId}`);
}

export async function getCartoonIcons() {
  return request("/cartoon/icons", {
    baseUrl: CARTOON_API_BASE_URL,
  });
}

export async function getCartoonSelection(userId, projectId) {
  if (!userId) {
    throw new Error("userId is required to load cartoon selection");
  }

  if (!projectId) {
    throw new Error("projectId is required to load cartoon selection");
  }

  const query = new URLSearchParams({
    userId: String(userId),
    projectId: String(projectId),
  });

  return request(`/cartoon-selection?${query.toString()}`, {
    baseUrl: CARTOON_API_BASE_URL,
  });
}

export async function saveCartoonSelection({
  userId,
  projectId,
  selections,
  matchedCartoon,
}) {
  if (!userId) {
    throw new Error("userId is required to save cartoon selection");
  }

  if (!projectId) {
    throw new Error("projectId is required to save cartoon selection");
  }

  return request("/cartoon-selection", {
    baseUrl: CARTOON_API_BASE_URL,
    method: "PUT",
    body: JSON.stringify({
      userId,
      projectId,
      selections: {
        ageGroup: selections?.ageGroup ?? "",
        gender: selections?.gender ?? "",
        skinTone: selections?.skinTone ?? "",
        hairColor: selections?.hairColor ?? "",
        hairStyle: selections?.hairStyle ?? "",
        eyeColor: selections?.eyeColor ?? "",
      },
      matchedCartoon: matchedCartoon
        ? {
            id: matchedCartoon.id || matchedCartoon._id || null,
          }
        : null,
    }),
  });
}

export async function deleteCartoonSelection(userId, projectId) {
  if (!userId) {
    throw new Error("userId is required to delete cartoon selection");
  }

  if (!projectId) {
    throw new Error("projectId is required to delete cartoon selection");
  }

  const query = new URLSearchParams({
    userId: String(userId),
    projectId: String(projectId),
  });

  return request(`/cartoon-selection?${query.toString()}`, {
    baseUrl: CARTOON_API_BASE_URL,
    method: "DELETE",
  });
}

// =============================================================================
// Default export
// =============================================================================

const api = {
  // Bootstrap / auth
  apiBackendLogin,
  apiLogin,
  apiSignup,
  apiForgotPassword,
  getMe,
  apiGetParentMe,
  getProjectDashboard,

  // Child
  updateChild,

  // Photos & captions
  presignParentPhotos,
  uploadToPresignedUrl,
  confirmUpload,
  getParentPhotos,
  deleteParentPhoto,
  getCaption,
  createCaption,
  updateCaption,
  deleteCaption,

  // Letter
  getLetters,
  getLetter,
  createLetter,
  updateLetter,
  deleteLetter,
  saveLetter,

  // Memories
  getMemories,
  createMemory,
  updateMemory,
  deleteMemory,
  reorderMemories,

  // School
  getSchoolPhotos,
  updateSchoolPhotoSelections,

  // Cover
  updateCover,

  // Shipping
  getAddresses,
  getDefaultShippingAddress,
  createAddress,
  updateAddress,

  // Order
  updateOrderQuantity,
  createCheckout,
  createOrderAmendment,

  // Stripe checkout / Yearbook Plus
  getCheckoutQuote,
  createStripePaymentIntent,
  createUpgradePaymentIntent,
  finalizeStripePayment,
  getCartoons,
  getOrderItems,

  getCartoonIcons,
  getCartoonSelection,
  saveCartoonSelection,
  deleteCartoonSelection,

  _get,
  _patch,
  _post,
  _delete,
};

export default api;